import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

interface WmsOrderTrackerProps {
  wmsOrderStatus: string;
  statoEvasione: string;
  wmsOrderNumber: string;
}

const STEPS = [
  { key: 'created', label: 'Ordine Creato', statuses: ['DRAFT'] },
  { key: 'confirmed', label: 'Confermato', statuses: ['CONFIRMED'] },
  { key: 'preparation', label: 'In Preparazione', statuses: ['ALLOCATED', 'PICKING'] },
  { key: 'picked', label: 'Prelevato', statuses: ['PICKED'] },
  { key: 'packing', label: 'In Imballaggio', statuses: ['PACKING'] },
  { key: 'shipped', label: 'Spedito', statuses: ['SHIPPED'] },
];

const CANCELLED_STATUSES = ['CANCELLED'];

function getStepIndex(wmsStatus: string): number {
  if (CANCELLED_STATUSES.includes(wmsStatus)) return -1;
  for (let i = STEPS.length - 1; i >= 0; i--) {
    if (STEPS[i].statuses.includes(wmsStatus)) return i;
  }
  return 0;
}

const WmsOrderTracker: React.FC<WmsOrderTrackerProps> = ({
  wmsOrderStatus,
  statoEvasione,
  wmsOrderNumber,
}) => {
  const isCancelled = CANCELLED_STATUSES.includes(wmsOrderStatus) || statoEvasione === 'annullato';
  const currentIndex = getStepIndex(wmsOrderStatus);

  if (isCancelled) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Ordine:</span>
          <span className="font-mono font-medium">{wmsOrderNumber}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Annullato
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Ordine:</span>
        <span className="font-mono font-medium">{wmsOrderNumber}</span>
      </div>
      <div className="flex items-center">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentIndex;
          const isActive = idx === currentIndex;
          const isPending = idx > currentIndex;

          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isActive
                      ? 'bg-blue-500 text-white ring-2 ring-blue-200'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`mt-1 text-[10px] text-center leading-tight max-w-[60px] ${
                    isActive ? 'text-blue-700 font-medium' : isPending ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 mt-[-14px] ${
                    idx < currentIndex ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default WmsOrderTracker;
